import React from 'react'
import PropTypes from 'prop-types'

import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'

import update from 'immutability-helper'
import { filter, intersectionBy, map, isEmpty } from 'lodash'

import { faArrowRight, faCalendar } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Icon from '@scoir/components-core/dist/components/Icon'
import { NonDismissibleDialog } from '@scoir/modals'

import scoirLogoIcon from 'icons/scoir-logo.svg'

const { Body, Footer } = NonDismissibleDialog
const { CloseButton, PrimaryActionButton } = Footer

const generateState = (props) => {
  const isCalendarFound = findCalendar(props.writeCalendars, props.writeCalendarId)
  return {
    writeCalendarId: isCalendarFound ? props.writeCalendarId : '',
    selectedCalendars: {},
    hasError: false,
    isSaving: false,
  }
}
const getSelectedReadCalendars = (readCalendars, isSelectedById) => {
  return filter(readCalendars, (calendar) => isSelectedById[calendar.Id])
}

const findCalendar = (array, id) => array.find((calendar) => calendar.Id === id)

export default class PreferredCalendarsModal extends React.Component {
  constructor(props) {
    super(props)
    this.state = generateState(props)
  }
  static defaultProps = {
    viewCalendars: [],
    readCalendars: [],
    writeCalendars: [],
  }
  static propTypes = {
    readCalendars: PropTypes.array.isRequired,
    writeCalendars: PropTypes.array.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onRemoveCalendarCredentials: PropTypes.func.isRequired,
    writeCalendarId: PropTypes.string,
    viewCalendars: PropTypes.arrayOf(PropTypes.object),
  }
  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      const newState = generateState(this.props)
      const selectedCalendars = update(newState.selectedCalendars, {
        $toggle: map(intersectionBy(this.props.readCalendars, this.props.viewCalendars, 'Id'), 'Id'),
      })
      this.setState({
        ...newState,
        selectedCalendars,
      })
    }
  }
  selectCalendar(calendar) {
    const selectedCalendars = update(this.state.selectedCalendars, {
      $toggle: [calendar.Id],
    })
    this.setState({ selectedCalendars })
  }
  onSave() {
    const writeCalendar = findCalendar(this.props.writeCalendars, this.state.writeCalendarId)
    const readCalendars = getSelectedReadCalendars(this.props.readCalendars, this.state.selectedCalendars)
    this.setState({
      hasError: false,
      isSaving: true,
    })
    this.props
      .onSave(writeCalendar, readCalendars)
      .catch(() => {
        this.setState({
          hasError: true,
        })
      })
      .finally(() => {
        this.setState({
          isSaving: false,
        })
      })
  }
  onCancel() {
    const { writeCalendarId, onClose, onRemoveCalendarCredentials } = this.props
    if (isEmpty(writeCalendarId)) {
      onRemoveCalendarCredentials()
    }
    onClose()
  }
  render() {
    const areDisplayCalendarsInvalid =
      getSelectedReadCalendars(this.props.readCalendars, this.state.selectedCalendars).length > 3
    const isReadCalendarInvalid = this.state.writeCalendarId === ''
    return (
      <NonDismissibleDialog
        open={this.props.isOpen}
        onClose={() => this.onCancel()}
        scroll="paper"
        headerText="Setup Calendar Sync"
      >
        <Body>
          <Box
            mb={2}
            color="primary.main"
            display="flex"
            alignItems="center"
            justifyContent="center"
            aria-hidden
          >
            <Icon
              size="lg"
              def={scoirLogoIcon}
              afterText
            />

            <Box
              mx={1}
              component="span"
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </Box>
            <FontAwesomeIcon
              size="2x"
              icon={faCalendar}
            />
          </Box>
          <Box mb={1}>
            <Typography variant="h6">Sync and display scheduled Scoir Visits in an external calendar.</Typography>
          </Box>
          <Typography>
            Scheduled Scoir Visits will be sent to the selected Calendar and visible to all its authorized users.
          </Typography>
          <TextField
            select
            name="writeCalendarId"
            value={this.state.writeCalendarId}
            onChange={(e) => this.setState({ writeCalendarId: e.target.value })}
            margin="normal"
            variant="outlined"
            color="primary"
            fullWidth
            label="Select a calendar"
          >
            <MenuItem value="">Select a calendar</MenuItem>
            {this.props.writeCalendars.map((calendar) => (
              <MenuItem
                key={calendar.Id}
                value={calendar.Id}
              >
                {calendar.Label}
              </MenuItem>
            ))}
          </TextField>
          <Box
            mt={6}
            mb={2}
            color="primary.main"
            display="flex"
            alignItems="center"
            justifyContent="center"
            aria-hidden
          >
            <FontAwesomeIcon
              icon={faCalendar}
              size="2x"
            />
            <Box
              mx={1}
              component="span"
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </Box>
            <Icon
              def={scoirLogoIcon}
              size="lg"
            />
          </Box>
          <Box mb={1}>
            <Typography variant="h6">Display other calendars in Scoir</Typography>
          </Box>
          <Typography
            role={areDisplayCalendarsInvalid ? 'alert' : undefined}
            color={areDisplayCalendarsInvalid ? 'error' : 'textPrimary'}
          >
            Select up to 3 calendars to display. They will only be visible to you.
          </Typography>
          <FormControl component="fieldset">
            {this.props.readCalendars.map((calendar) => (
              <FormControlLabel
                label={calendar.Label}
                key={calendar.Id}
                control={
                  <Checkbox
                    checked={this.state.selectedCalendars[calendar.Id] || false}
                    onChange={() => this.selectCalendar(calendar)}
                    color="primary"
                    disableRipple={false}
                  />
                }
              />
            ))}
          </FormControl>
          {this.state.hasError && (
            <Box my={1}>
              <Alert
                severity="error"
                icon={false}
              >
                An error occurred while saving your selected calendars. Please try again later.
              </Alert>
            </Box>
          )}
        </Body>
        <Footer>
          {this.state.isSaving && (
            <Box textAlign="center">
              <CircularProgress
                aria-label="Loading"
                role="status"
                size="20px"
              />
            </Box>
          )}
          <CloseButton>Cancel</CloseButton>
          <PrimaryActionButton
            disabled={isReadCalendarInvalid || areDisplayCalendarsInvalid}
            onClick={() => this.onSave()}
          >
            Save
          </PrimaryActionButton>
        </Footer>
      </NonDismissibleDialog>
    )
  }
}
